import {
  AccountAlertStats,
  AccountFull,
  CurrentUserAccount,
  CreateAccountDto,
  UpdateAccountDto
} from '@/models/account.model';
import { TimeLimitedLinkResponse } from '@/models/common.model';
import { Paginated, PaginationProps } from '@/models/paginated.model';
import { api } from '@/services/api/index';
import { sortByCriteriaToSnake } from '@/utils/api.util';

type URLResponse = { url: string };
type GetAccountsQueryArgs = PaginationProps;
type GetAccountQueryArgs = {
  accountId: string;
};
type GetAccountByNameQueryArgs = {
  accountName: string;
};
type CreateAccountMutationArgs = CreateAccountDto;
type UpdateAccountMutationArgs = GetAccountQueryArgs & UpdateAccountDto;
type DeleteAccountMutationArgs = {
  accountId: string;
};
type CreateAccountOnboardingLinkMutationArgs = GetAccountQueryArgs & {
  refreshUrl: string;
  returnUrl: string;
};
type GetAccountImportReportEmbeddingLinkQueryArgs = GetAccountQueryArgs;

export const accountApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getAccounts: builder.query<Paginated<AccountFull>, GetAccountsQueryArgs>({
      query: ({ page, size, search, sortBy }) => ({
        url: `/v1/accounts`,
        method: 'GET',
        params: {
          page: page,
          size: size,
          search: search || undefined,
          sortBy:
            sortBy?.map((key) => sortByCriteriaToSnake(key))?.join(',') ||
            undefined
        }
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.items.map(({ id }) => ({
                type: 'Account' as const,
                id: id
              })),
              {
                type: 'Account' as const,
                id: 'PARTIAL-LIST'
              }
            ]
          : []
    }),
    getAccount: builder.query<AccountFull, GetAccountQueryArgs>({
      query: ({ accountId }) => ({
        url: `/v1/accounts/${accountId}`,
        method: 'GET'
      }),
      providesTags: (result) =>
        result
          ? [
              {
                type: 'Account' as const,
                id: result.id
              }
            ]
          : []
    }),
    getAccountByName: builder.query<AccountFull, GetAccountByNameQueryArgs>({
      query: ({ accountName }) => ({
        url: `/v1/accounts/name/${accountName}`,
        method: 'GET'
      }),
      providesTags: (result) =>
        result
          ? [
              {
                type: 'Account' as const,
                id: result.id
              }
            ]
          : []
    }),
    createAccount: builder.mutation<AccountFull, CreateAccountMutationArgs>({
      query: (payload) => ({
        url: '/v1/accounts',
        method: 'POST',
        data: payload
      }),
      invalidatesTags: [{ type: 'Account' as const, id: 'PARTIAL-LIST' }]
    }),
    updateAccount: builder.mutation<AccountFull, UpdateAccountMutationArgs>({
      query: ({ accountId, ...payload }) => ({
        url: `/v1/accounts/${accountId}`,
        method: 'PATCH',
        data: payload
      }),
      invalidatesTags: (result) =>
        result ? [{ type: 'Account' as const, id: result.id }] : []
    }),
    deleteAccount: builder.mutation<void, DeleteAccountMutationArgs>({
      query: ({ accountId }) => ({
        url: `/v1/accounts/${accountId}`,
        method: 'DELETE'
      }),
      invalidatesTags: [{ type: 'Account' as const, id: 'PARTIAL-LIST' }]
    }),
    createAccountOnboardingLink: builder.mutation<
      URLResponse,
      CreateAccountOnboardingLinkMutationArgs
    >({
      query: ({ accountId, ...data }) => ({
        url: `/v1/accounts/${accountId}/onboarding-link`,
        method: 'POST',
        data: data
      })
    }),
    createAccountLoginLink: builder.mutation<URLResponse, GetAccountQueryArgs>({
      query: ({ accountId }) => ({
        url: `/v1/accounts/${accountId}/login-link`,
        method: 'POST'
      })
    }),
    getAccountAlertStats: builder.query<AccountAlertStats, GetAccountQueryArgs>(
      {
        query: ({ accountId }) => ({
          url: `/v1/accounts/${accountId}/alert-stats`,
          method: 'GET'
        }),
        providesTags: (result) =>
          result ? [{ type: 'Account' as const, id: 'ALERT-STATS' }] : []
      }
    ),
    getAccountImpactReportEmbeddingLink: builder.query<
      TimeLimitedLinkResponse,
      GetAccountImportReportEmbeddingLinkQueryArgs
    >({
      query: ({ accountId }) => ({
        url: `/v1/accounts/${accountId}/impact-report-embedding-link`,
        method: 'GET'
      }),
      providesTags: (result, _err, arg) =>
        result
          ? [{ type: 'Account' as const, id: `IMPACT-REPORT/${arg.accountId}` }]
          : []
    }),
    getMyAccounts: builder.query<Paginated<CurrentUserAccount>, void>({
      query: () => ({
        url: `/v1/accounts/me`,
        method: 'GET'
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.items.map(({ id }) => ({
                type: 'Account' as const,
                id: id
              })),
              {
                type: 'Account' as const,
                id: 'MY-ACCOUNTS'
              }
            ]
          : []
    })
  })
});

export const {
  useGetAccountsQuery,
  useGetAccountQuery,
  useGetAccountByNameQuery,
  useCreateAccountMutation,
  useUpdateAccountMutation,
  useDeleteAccountMutation,
  useCreateAccountOnboardingLinkMutation,
  useCreateAccountLoginLinkMutation,
  useGetAccountAlertStatsQuery,
  useGetAccountImpactReportEmbeddingLinkQuery,
  useGetMyAccountsQuery
} = accountApi;
